
import { Injectable } from '@angular/core';

import { Observable, shareReplay, of, merge, throwError, EMPTY } from 'rxjs';
import { map, switchMap, catchError, tap, take } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { Digest } from '@Monitor/Briefings/models/digest.model';
import { AllResourceAbstractService } from '@Common/Basic/service/all-resource-abstract.service';
import { DigestApi } from '@api/briefings/model/digest-api';
import { DigestApiService } from '@api/digest/digest.api.service';
import { ResourceAction } from '@Common/Basic/service/resource-abstract.service';
import { MultiBaseAbstractApi } from '@api/genericApi/multi-base-abstract.api';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { DigestSubscriberApiService } from '@api/digest/digest-subscriber.api.service';
import { DigestSubscriberApi } from '@api/briefings/model/digest-subscriber-api';
import { DigestPatchApi } from '@api/briefings/model/digest-patch-api';
import { DigestShareRequestApi } from '@api/briefings/model/digest-share-request-api';
import { ResourceActionTypes } from '@Common/Basic/enums/resource-actions.enum';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { ConfirmDialogModel } from '@Common/Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '@Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { CommonApiErrorEnum } from '@api/genericApi/enums/common-api-error.enum';

@Injectable()
export class DigestService extends AllResourceAbstractService<Digest, DigestApi, Digest, DigestApi>  {

    all$: Observable<Digest[]> = merge(
        this.fetchList$(),
        this.refreshAllManualSubject
    ).pipe(
        shareReplay(1)
    );

    constructor(
        private digestApiService: DigestApiService,
        private digestSubscriberApiService: DigestSubscriberApiService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        private coreDialogService: CoreDialogService,
    ) {
        super();
    }

    protected fetchList$(): Observable<Digest[]> {
        return this.getApiService().getList().pipe(
            map(list => list
                .map(item => this.constructListModel(item))
                .sort((a, b) => a.name.localeCompare(b.name))
            ),
            catchError((error) => {
                if (error.status === 404) {
                    return of([]);
                }
                throw error;
            })
        );
    }

    getApiService(): MultiBaseAbstractApi<DigestApi, DigestApi> {
        return this.digestApiService;
    }

    constructListModel(apiModel: DigestApi): Digest {
        return new Digest(apiModel);
    }

    constructResourceModel(apiModel: DigestApi): Digest {
        return new Digest(apiModel);
    }

    refreshAllManual(): Observable<Digest[]> {
        return this.fetchList$().pipe(
            tap((list) => this.refreshAllManualSubject.next(list)),
        );
    }

    create(digest: Digest): Observable<number> {
        return this.isNameDuplicated$(digest.name).pipe(
            switchMap(isDuplicate => {
                if (isDuplicate) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Briefings.digest.duplicateName.error.heading',
                        detail: this.localizationService.transform('Briefings.digest.duplicateName.error.message', {
                            name: digest.name
                        })
                    });

                    throw new Error(CommonApiErrorEnum.duplicateName);
                }

                return super.createResource(digest).pipe(
                    switchMap((id) => this.refreshAllManual().pipe(map(() => id)))
                );
            })
        );
    }


    update(digest: Digest): Observable<number> {
        const action: ResourceAction = { actionType: ResourceActionTypes.UPDATE };

        return this.all$.pipe(
            take(1),
            switchMap(all => {
                const existing = all.find(x => x.id === digest.id);
                const nameChanged = existing?.name !== digest.name;

                if (!nameChanged) {
                    return super.updateResource(digest, action);
                }

                return this.isNameDuplicated$(digest.name, digest.id).pipe(
                    switchMap(isDuplicate => {
                        if (isDuplicate) {
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: 'Briefings.digest.duplicateName.error.heading',
                                detail: this.localizationService.transform('Briefings.digest.duplicateName.error.message', {
                                    name: digest.name
                                })
                            });

                            throw new Error(CommonApiErrorEnum.duplicateName);
                        }

                        return super.updateResource(digest, action);
                    })
                );
            }),
            switchMap((id) => this.refreshAllManual().pipe(map(() => id)))
        );
    }

    share(digest: Digest, digestShareRequest: DigestShareRequestApi) {
        const action: ResourceAction = { actionType: ResourceActionTypes.SHARE, resource: digest };

        return this.digestApiService.shareDigest(digest.id, digestShareRequest).pipe(
            tap(() => {
                this.actionCompletedSubject.next(action);
            }),
            catchError(err => {
                this.actionCompletedSubject.next({
                    ...action,
                    error: err,
                });
                throw new Error(err);
            })
        );
    }

    delete(digest: Digest): Observable<number> {
        const action: ResourceAction = { actionType: ResourceActionTypes.DELETE, resource: digest };

        return this.deleteResource(digest.id, action).pipe(
            switchMap((id) => this.refreshAllManual().pipe(map(() => id))),
        );
    }

    openDeleteConfirmationDialog(digests: Digest[]) {
        const io: ConfirmDialogModel = {
            inputs: {
                heading: 'Briefings.manageDigestDialog.delete.heading',
                alertType: messageSeverityEnum.error,
                alertMessage: 'Briefings.manageDigestDialog.delete.alertMessage',
                showCancel: true,
                message: 'Briefings.manageDigestDialog.delete.message',
                list: digests.map(item => item.name),
                okLabel: 'Core.button.delete',
                confirmAction: () => {
                    if (digests.length === 1) {
                        return this.delete(digests[0]);
                    } else {
                        return this.bulkDeleteResource(digests);
                    }
                }
            },
            outputs: {
                dialogClose: () => {}
            }
        };

        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    getSubscribersByDigestId(digestId: number): Observable<DigestSubscriberApi[]> {
        return this.digestApiService.getSubscribersByDigestId(digestId);
    }

    updateSubscriber(subscriber: DigestSubscriberApi): Observable<number> {
        return this.digestSubscriberApiService.update(subscriber);
    }

    unsubscribe(digestId: number, subscriberId: number): Observable<number> {
        return this.digestSubscriberApiService.unsubscribeById(digestId, subscriberId).pipe(
            tap(() => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Briefings.digest.subscribers.remove.heading'
                });
            }),
            catchError((error) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Briefings.digest.subscribers.remove.error.heading'
                });

                return throwError(() => new Error(error));
            })
        );
    }


    getDigestsByBriefingId(briefingId: number): Observable<DigestApi[]> {
        return this.digestApiService.getDigestsByBriefingId(briefingId);
    }

    patchDigestsForBriefing(briefingId: number, digestPatchApi: DigestPatchApi): Observable<void> {
        return this.digestApiService.patchDigestsForBriefing(briefingId, digestPatchApi);
    }

    override bulkDeleteResource(digests: Digest[]): Observable<number[]> {
        const action: ResourceAction = {
            actionType: ResourceActionTypes.BULK_DELETE,
            count: digests.length
        };

        return this.getApiService().deleteByIds(digests.map(digest => digest.id)).pipe(
            tap(() => {
                this.actionCompletedSubject.next(action);
            }),
            catchError(err => {
                this.actionCompletedSubject.next({
                    ...action,
                    error: err
                });
                return of(null);
            }),
            switchMap((id) => this.refreshAllManual().pipe(map(() => id)))
        );
    }

    messageCenter(actionResult: ResourceAction) {
        const actionType = actionResult.actionType;
        const error = actionResult.error;
        const resource = actionResult.resource as Digest;

        switch(actionType) {
            case ResourceActionTypes.CREATE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Briefings.digest.create.error.heading',
                        detail: this.localizationService.transform('Briefings.digest.create.error.message', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Briefings.digest.create.heading',
                        detail: this.localizationService.transform('Briefings.digest.create.message', {
                            name: resource.name
                        })
                    });
                }

                break;
            }

            case ResourceActionTypes.UPDATE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Briefings.digest.update.error.heading',
                        detail: this.localizationService.transform('Briefings.digest.update.error.message', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Briefings.digest.update.heading',
                        detail: this.localizationService.transform('Briefings.digest.update.message', {
                            name: resource.name
                        })
                    });
                }

                break;
            }

            case ResourceActionTypes.DELETE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Briefings.digest.delete.error.heading',
                        detail: this.localizationService.transform('Briefings.digest.delete.error.message', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Briefings.digest.delete.heading',
                        detail: this.localizationService.transform('Briefings.digest.delete.message', {
                            name: resource.name
                        })
                    });
                }

                break;
            }

            case ResourceActionTypes.BULK_DELETE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Briefings.digest.bulkDelete.error.heading',
                        detail: 'Briefings.digest.bulkDelete.error.message'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Briefings.digest.bulkDelete.heading',
                        detail: this.localizationService.transform('Briefings.digest.bulkDelete.message', {
                            number: actionResult.count
                        })
                    });
                }

                break;
            }

            case ResourceActionTypes.SHARE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Briefings.digest.share.error.heading',
                        detail: this.localizationService.transform('Briefings.digest.share.error.message', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Briefings.digest.share.heading',
                        detail: this.localizationService.transform('Briefings.digest.share.message', {
                            name: resource.name
                        })
                    });
                }

                break;
            }
        }
    }
}
